// HTML editor widths are screen pixels, not PDF column widths. Keep the table
// structure (including merged cells) while sizing it inside its enclosing cell.
export function normaliseReportHtml(nodes) {
  function visit(value, inTable = false, tableFontSize) {
    if (inTable && typeof value === 'string' && /^[\r\n\t]+$/.test(value)) return null;
    if (Array.isArray(value)) return value.map(item => visit(item, inTable, tableFontSize)).filter(item => item !== null);
    if (!value || typeof value !== 'object') return value;
    const node = { ...value };
    if (tableFontSize) node.fontSize = tableFontSize;
    const tag = node.nodeName;
    if (inTable && !tag && typeof node.text === 'string' && /^[\r\n\t]+$/.test(node.text)) return null;
    if (['P', 'DIV'].includes(tag) && typeof node.text === 'string' && !node.text.trim()) return null;
    if (tag && tag !== 'IMG') {
      delete node.width;
      delete node.height;
      delete node.marginLeft;
      delete node.marginRight;
      delete node.marginTop;
      delete node.marginBottom;
      node.margin = ['P', 'DIV', 'UL', 'OL'].includes(tag) ? [0, 0, 0, inTable ? 2 : 4] : [0, 0, 0, 0];
      node.lineHeight = 1.15;
      node.noWrap = false;
    }
    if (node.table) {
      const count = Math.max(...node.table.body.map(row => row.length));
      const firstRow = node.table.body[0] || [];
      const oldWidths = node.table.widths || firstRow.map(cell => cell.width);
      const numeric = oldWidths.map(width => typeof width === 'number' ? width : parseFloat(width));
      const proportional = numeric.length === count && numeric.every(width => Number.isFinite(width) && width > 0);
      const total = proportional ? numeric.reduce((a, b) => a + b, 0) : count;
      node.table = { ...node.table, widths: Array.from({ length: count }, (_, i) => `${100 * (proportional ? numeric[i] : 1) / total}%`), body: visit(node.table.body, true, count > 6 ? 8 : tableFontSize) };
      delete node.table.heights;
      node.margin = [0, 2, 0, 4];
      node.layout = { paddingLeft: () => 2, paddingRight: () => 2, paddingTop: () => 2, paddingBottom: () => 2 };
    }
    for (const key of ['stack', 'text', 'ul', 'ol', 'columns']) {
      if (node[key] && typeof node[key] === 'object') node[key] = visit(node[key], inTable, tableFontSize);
    }
    return node;
  }
  return visit(nodes);
}

// Put a section break on the next section, rather than at the end of nested
// columns. Nested trailing breaks can create an otherwise empty PDF page.
export function normaliseReportPageBreaks(definition) {
  function takeTrailingBreak(node) {
    if (!node || typeof node !== 'object') return false;
    let found = node.pageBreak === 'after';
    if (found) delete node.pageBreak;
    if (Array.isArray(node)) {
      if (node.length) found = takeTrailingBreak(node[node.length - 1]) || found;
    } else {
      if (node.stack) found = takeTrailingBreak(node.stack) || found;
      if (node.columns) node.columns.forEach(column => { found = takeTrailingBreak(column) || found; });
      if (node.table && node.table.body.length) {
        node.table.body[node.table.body.length - 1].forEach(cell => { found = takeTrailingBreak(cell) || found; });
      }
    }
    return found;
  }
  let previousBreak = false;
  // pdfmake ignores pageBreak properties attached to arrays. Wrap them in
  // real stack nodes before moving a preceding section break onto them.
  definition.content = definition.content.map(section => Array.isArray(section) ? { stack: section } : section);
  definition.content.forEach((section, index) => {
    const nextBreak = takeTrailingBreak(section);
    if (previousBreak && index) section.pageBreak = 'before';
    previousBreak = nextBreak;
  });
  return definition;
}

export function downloadReportPdf(pdfMake, definition, reference) {
  const filename = `${String(reference || 'Asset-Verification-Report').replace(/[^a-zA-Z0-9_-]/g, '-')}.pdf`;
  pdfMake.createPdf(normaliseReportPageBreaks(definition)).download(filename);
}
